import { useState, useMemo } from "react";
import { ArrowLeft, Upload, ChevronDown, ChevronUp, Sparkles, Lock, ClipboardPaste, Repeat } from "lucide-react";
import { readTables, readText, readRaw, flipDirection, detectRecurringCosts, merchantKey } from "./smartImport.js";
import { categorizeRows, buildHistory } from "./categorizer.js";
import { readAnyFile, readPastedText } from "./fileReaders.js";
import { aiCategorize, aiExtract, UPGRADE_URL } from "./aiImport.js";

const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const prettyDate = (iso) => `${MON[+iso.slice(5, 7) - 1]} ${+iso.slice(8, 10)}`;
const prettyLong = (iso) => `${MON[+iso.slice(5, 7) - 1]} ${+iso.slice(8, 10)}, ${iso.slice(0, 4)}`;

function nextOccurrenceAfterToday(lastDateStr, frequency) {
  const d = new Date(lastDateStr + "T00:00:00");
  const today = new Date(localToday() + "T00:00:00");
  let guard = 0;
  while (d <= today && guard < 60) {
    if (frequency === "weekly") d.setDate(d.getDate() + 7); else d.setMonth(d.getMonth() + 1);
    guard++;
  }
  return toISO(d);
}

const selectStyle = { background: "var(--card2)", color: "var(--ivory)", border: "1px solid var(--hair)" };

export default function ImportView({ active, updateActive, fmt, setView, showToast, session, accountPlan, categoryColor }) {
  const [stage, setStage] = useState("pick"); // pick | reading | review | error
  const [fileName, setFileName] = useState("");
  const [baseRows, setBaseRows] = useState([]);
  const [detection, setDetection] = useState(null);
  const [flipped, setFlipped] = useState(false);
  const [overrides, setOverrides] = useState({});      // merchantKey -> category (user's own choice)
  const [rowOverrides, setRowOverrides] = useState({}); // row id -> category (one-off)
  const [aiMap, setAiMap] = useState({});              // merchant (lowercase) -> category
  const [inclusion, setInclusion] = useState({});      // row id -> boolean
  const [recurringOff, setRecurringOff] = useState({});
  const [open, setOpen] = useState({});
  const [showSkipped, setShowSkipped] = useState(false);
  const [showHow, setShowHow] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [error, setError] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [rawText, setRawText] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [stmtOverride, setStmtOverride] = useState(""); // YYYY-MM-DD the person pinned the statement date to

  const isPaid = accountPlan === "paid" && !!session;
  const isBusiness = active.type === "business";
  const locale = typeof navigator !== "undefined" ? navigator.language : "en-US";

  function reset() {
    setStage("pick"); setFileName(""); setBaseRows([]); setDetection(null); setFlipped(false);
    setOverrides({}); setRowOverrides({}); setAiMap({}); setInclusion({}); setRecurringOff({});
    setOpen({}); setShowSkipped(false); setShowHow(false); setPasteText(""); setError(""); setRawText(""); setStmtOverride("");
  }

  function applyResult(res, name) {
    if (res.failed || res.rows.length === 0) return false;
    setBaseRows(res.rows); setDetection(res.detection); setFileName(name); setStage("review");
    return true;
  }

  async function handleSource(src) {
    setError(""); setRawText("");
    try {
      let res;
      if (src.kind === "tables") {
        res = readTables(src.tables, { localeHint: locale });
        // A spreadsheet that's really a text dump — try the line reader before giving up
        if (res.failed) {
          const asText = src.tables.map((t) => t.rows.map((r) => r.filter((c) => c !== "" && c != null).join("  ")).join("\n")).join("\n");
          setRawText(asText);
          res = readText(asText, { localeHint: locale });
        }
      } else {
        setRawText(src.text);
        res = readText(src.text, { localeHint: locale });
      }
      if (!applyResult(res, src.name)) {
        setStage("error");
        setError(src.kind === "text" && !src.text.trim()
          ? "That file has no readable text — it may be a scanned image."
          : "We couldn't find a date and an amount anywhere in that file.");
      }
    } catch (e) {
      console.error(e);
      setStage("error");
      setError("That file couldn't be opened. Try exporting it again as CSV or Excel.");
    }
  }

  // Re-read a text/PDF statement against a statement date the person typed in:
  // month and day stay as printed, the year follows the statement.
  function applyStatementDate(isoDate) {
    setStmtOverride(isoDate);
    if (!rawText) return;
    const res = readText(rawText, { localeHint: locale, statementDate: isoDate || null, force: !!isoDate });
    if (res.failed || res.rows.length === 0) return;
    setBaseRows(res.rows); setDetection(res.detection); setInclusion({}); setRowOverrides({});
  }

  async function onFile(file) {
    if (!file) return;
    setStage("reading");
    try { await handleSource(await readAnyFile(file)); }
    catch (e) { console.error(e); setStage("error"); setError("That file couldn't be opened."); }
  }

  async function onPaste() {
    if (!pasteText.trim()) return;
    setStage("reading");
    await handleSource(readPastedText(pasteText));
  }

  async function tryAiExtract() {
    setAiBusy(true);
    try {
      const raw = await aiExtract(rawText);
      if (!applyResult(readRaw(raw), fileName || "AI reading")) setError("AI couldn't find transactions in that file either.");
    } catch (e) { setError(e.message); }
    setAiBusy(false);
  }

  // ---- everything below is derived, so flipping / editing is instant ----
  const categories = active.categories;

  const model = useMemo(() => {
    if (stage !== "review") return null;
    const rows = flipped ? flipDirection(baseRows) : baseRows;
    const spend = rows.filter((r) => r.kind === "expense" || r.kind === "transfer");
    const history = buildHistory(active.expenses, categories);
    const { rows: cat, newCategories } = categorizeRows(spend, { categories, history });
    const byId = new Map(cat.map((r) => [r.id, r]));

    // duplicates against what's already in the ledger
    const seen = new Map();
    active.expenses.forEach((e) => { const k = `${e.date}|${Number(e.amount).toFixed(2)}`; seen.set(k, (seen.get(k) || 0) + 1); });

    const known = new Set(categories.map((c) => c.toLowerCase()));
    const items = rows.map((r) => {
      if (r.kind === "income") return { ...r, category: null, confidence: null, duplicate: false };
      const c = byId.get(r.id);
      let category = c.category, confidence = c.confidence;
      const mk = merchantKey(r.raw);
      if (rowOverrides[r.id]) { category = rowOverrides[r.id]; confidence = "history"; }
      else if (mk && overrides[mk]) { category = overrides[mk]; confidence = "history"; }
      else if (confidence === "unsure" && aiMap[r.merchant.toLowerCase()]) { category = aiMap[r.merchant.toLowerCase()]; confidence = "ai"; }
      const k = `${r.date}|${r.amount.toFixed(2)}`;
      let duplicate = false;
      if ((seen.get(k) || 0) > 0) { duplicate = true; seen.set(k, seen.get(k) - 1); }
      return { ...r, category, confidence, duplicate, _newCat: !known.has(String(category).toLowerCase()) };
    });
    return { items, backendNew: newCategories };
  }, [stage, baseRows, flipped, overrides, rowOverrides, aiMap, active.expenses, categories]);

  const view = useMemo(() => {
    if (!model) return null;
    const isIncluded = (r) => {
      if (inclusion[r.id] !== undefined) return inclusion[r.id];
      return r.kind === "expense" && !r.duplicate; // transfers & income are opt-in
    };
    const items = model.items.map((r) => ({ ...r, included: isIncluded(r) }));
    const isSpend = (r) => r.kind === "expense" || r.kind === "transfer";
    const expenses = items.filter((r) => isSpend(r) && r.included);
    const incomeIn = items.filter((r) => r.kind === "income" && r.included);
    const skipped = items.filter((r) => !r.included);
    

    const unsure = expenses.filter((r) => r.confidence === "unsure");
    const groups = {};
    expenses.filter((r) => r.confidence !== "unsure").forEach((r) => { (groups[r.category] = groups[r.category] || []).push(r); });
    const groupList = Object.entries(groups).map(([name, list]) => ({ name, list, total: list.reduce((s, r) => s + r.amount, 0) })).sort((a, b) => b.total - a.total);

    const newCats = [];
    const known = new Set(categories.map((c) => c.toLowerCase()));
    expenses.forEach((r) => { const c = r.category; if (c && !known.has(c.toLowerCase()) && !newCats.some((n) => n.toLowerCase() === c.toLowerCase())) newCats.push(c); });

    const dates = expenses.concat(incomeIn).map((r) => r.date).sort();
    const recurring = detectRecurringCosts(expenses.map((r) => ({ amount: r.amount, category: r.category, note: r.merchant, date: r.date })));
    return {
      items, expenses, incomeIn, skipped, groupList, unsure, newCats, recurring,
      total: expenses.reduce((s, r) => s + r.amount, 0),
      from: dates[0], to: dates[dates.length - 1],
      dupCount: items.filter((r) => isSpend(r) && r.duplicate && !r.included).length,
    };
  }, [model, inclusion, categories]);

  function setCategoryFor(row, category, allSame) {
    if (allSame) { const mk = merchantKey(row.raw); if (mk) { setOverrides((o) => ({ ...o, [mk]: category })); return; } }
    setRowOverrides((o) => ({ ...o, [row.id]: category }));
  }
  const toggleRow = (r) => setInclusion((m) => ({ ...m, [r.id]: !r.included }));

  async function runAi() {
    const merchants = [...new Set(view.unsure.map((r) => r.merchant))];
    if (!merchants.length) return;
    setAiBusy(true);
    try {
      const allowed = [...new Set([...categories, ...view.newCats])];
      const map = await aiCategorize(merchants, allowed);
      const lower = {};
      Object.entries(map).forEach(([m, c]) => { lower[m.toLowerCase()] = c; });
      setAiMap((prev) => ({ ...prev, ...lower }));
      showToast(`AI sorted ${Object.keys(map).length} of ${merchants.length}`);
    } catch (e) { showToast(e.message || "AI sort failed"); }
    setAiBusy(false);
  }

  function confirm() {
    const entries = view.expenses.map((r) => ({
      id: uid(), amount: r.amount, category: r.category, note: r.note || r.merchant, date: r.date,
      member: active.type === "household" ? "shared" : null,
      clientId: isBusiness ? "none" : null,
      deductible: isBusiness ? true : null,
      custom: {},
    }));
    const incomeEntries = isBusiness ? view.incomeIn.map((r) => ({ id: uid(), date: r.date, amount: r.amount, source: r.merchant, note: "Imported" })) : [];
    const recurringTemplates = view.recurring.filter((c) => !recurringOff[c.key]).map((c) => ({
      id: uid(), amount: c.amount, category: c.category, note: c.label, frequency: c.frequency,
      nextDate: nextOccurrenceAfterToday(c.lastDate, c.frequency),
    }));
    updateActive((p) => ({
      ...p,
      categories: [...p.categories, ...view.newCats.filter((n) => !p.categories.some((c) => c.toLowerCase() === n.toLowerCase()))],
      expenses: [...entries, ...p.expenses],
      income: [...incomeEntries, ...(p.income || [])],
      recurring: [...p.recurring, ...recurringTemplates],
    }));
    const bits = [`${entries.length} ${entries.length === 1 ? "entry" : "entries"}`];
    if (incomeEntries.length) bits.push(`${incomeEntries.length} income`);
    if (view.newCats.length) bits.push(`${view.newCats.length} new ${view.newCats.length === 1 ? "category" : "categories"}`);
    if (recurringTemplates.length) bits.push(`${recurringTemplates.length} recurring`);
    showToast(`Imported: ${bits.join(", ")}`);
    reset(); setView("dashboard");
  }

  const catOptions = [...new Set([...categories, ...(view ? view.newCats : [])])];

  // The full statement reference wraps onto as many lines as it needs; the category picker gets its own full-width line.
  const renderRow = (r) => (
    <div key={r.id} className="py-2.5 border-b border-hair last:border-0">
      <div className="flex items-start gap-2">
        <input type="checkbox" checked={r.included} onChange={() => toggleRow(r)} className="shrink-0 mt-1" />
        <div className="min-w-0 flex-1">
          <div className="text-sm break-words">{r.note || r.merchant}</div>
          <div className="text-xs text-ivory-dim">{prettyLong(r.date)}</div>
        </div>
        <div className="v-mono text-sm text-right shrink-0">{fmt(r.amount)}</div>
      </div>
      <div className="mt-1.5 pl-[21px]">
        <select
          value={r.category}
          onChange={(e) => {
            const same = view.items.filter((x) => x.kind !== "income" && merchantKey(x.raw) === merchantKey(r.raw) && x.id !== r.id).length;
            setCategoryFor(r, e.target.value, same > 0);
          }}
          className="rounded-md px-1.5 py-1 text-xs w-full"
          style={{ ...selectStyle, borderColor: r.confidence === "unsure" ? "var(--brass)" : "var(--hair)" }}
        >
          {catOptions.includes(r.category) ? null : <option value={r.category}>{r.category}</option>}
          {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
    </div>
  );

  const renderGroup = ({ id, title, dot, list, total, sub, defaultOpen }) => {
    const isOpen = open[id] ?? defaultOpen ?? false;
    return (
      <div key={id} className="bg-card rounded-xl mb-2 overflow-hidden">
        <button onClick={() => setOpen((o) => ({ ...o, [id]: !isOpen }))} className="w-full flex items-center gap-3 px-3.5 py-3 text-left">
          <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: dot }} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium break-words">{title}</div>
            <div className="text-xs text-ivory-dim">{list.length} {list.length === 1 ? "transaction" : "transactions"}{sub ? ` · ${sub}` : ""}</div>
          </div>
          <div className="v-mono text-sm shrink-0">{fmt(total)}</div>
          {isOpen ? <ChevronUp size={15} className="text-ivory-dim shrink-0" /> : <ChevronDown size={15} className="text-ivory-dim shrink-0" />}
        </button>
        {isOpen && (
          <div className="px-3.5 pb-2">
            {list.slice(0, 150).map((r) => renderRow(r))}
            {list.length > 150 && <div className="text-xs text-ivory-dim py-2">+ {list.length - 150} more (all will be imported)</div>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`px-5 pt-8 pb-28 ${stage === "review" ? "wide-cols" : ""}`}>
      <div className="flex items-center gap-3 mb-2">
        <button onClick={() => { reset(); setView("dashboard"); }} className="p-1 -ml-1"><ArrowLeft size={18} className="text-ivory-dim" /></button>
        <h2 className="v-display text-xl font-semibold">Import expenses</h2>
      </div>

      {(stage === "pick" || stage === "error") && (
        <div>
          <p className="text-ivory-dim text-xs mb-5">Drop in a bank or card statement and everything is read for you — dates, amounts, merchants and categories. No column picking. Works with CSV, Excel (every sheet) and PDF statements.</p>

          {stage === "error" && (
            <div className="rounded-2xl p-4 mb-4 text-xs leading-relaxed" style={{ background: "rgba(181,68,95,0.15)", border: "1px solid var(--rust)" }}>
              <div className="font-semibold mb-1" style={{ color: "var(--rust)" }}>{error}</div>
              <div className="text-ivory-dim">Try the statement as CSV or Excel from your bank's website, or paste the transactions below.</div>
              {rawText && (
                isPaid ? (
                  <div className="mt-3">
                    <button onClick={tryAiExtract} disabled={aiBusy} className="flex items-center gap-1.5 bg-gold-grad text-ink font-semibold rounded-full px-4 py-2 text-xs disabled:opacity-50">
                      <Sparkles size={13} /> {aiBusy ? "Reading…" : "Let AI read it"}
                    </button>
                    <div className="text-ivory-dim mt-1.5">This sends the file's text (including amounts) to our AI service to read it. Nothing is stored.</div>
                  </div>
                ) : UPGRADE_URL ? (
                  <a href={UPGRADE_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-brass font-medium"><Lock size={12} /> AI import can read files like this (paid)</a>
                ) : null
              )}
            </div>
          )}

          <label
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); onFile(e.dataTransfer.files?.[0]); }}
            className="w-full flex flex-col items-center justify-center gap-2 bg-card2 rounded-2xl py-10 cursor-pointer border"
            style={{ borderColor: dragOver ? "var(--brass)" : "var(--hair)", borderStyle: "dashed" }}
          >
            <Upload size={22} className="text-brass" />
            <span className="text-sm font-medium">Choose or drop a statement</span>
            <span className="text-xs text-ivory-dim">.csv · .xlsx · .xls · .pdf · .txt</span>
            <input type="file" accept=".csv,.tsv,.txt,.xlsx,.xls,.xlsm,.ods,.pdf" className="hidden" onChange={(e) => { onFile(e.target.files[0]); e.target.value = ""; }} />
          </label>

          <button onClick={() => setPasteOpen((v) => !v)} className="flex items-center gap-1.5 text-xs text-brass font-medium mt-4">
            <ClipboardPaste size={13} /> {pasteOpen ? "Hide paste box" : "Or paste transactions from your bank's website"}
          </button>
          {pasteOpen && (
            <div className="mt-3">
              <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={7} placeholder={"Copy the transaction list from your online banking and paste it here — rows from a spreadsheet work too."} className="w-full rounded-lg px-3 py-2 text-xs v-mono" style={{ background: "var(--card2)", color: "var(--ivory)", border: "1px solid var(--hair)" }} />
              <button onClick={onPaste} disabled={!pasteText.trim()} className="mt-2 bg-gold-grad text-ink font-semibold rounded-full px-5 py-2 text-sm disabled:opacity-40">Read it</button>
            </div>
          )}

          <div className="bg-card rounded-xl p-3.5 mt-6 text-xs text-ivory-dim leading-relaxed">
            <div className="text-ivory font-medium mb-1">What happens automatically</div>
            Finds the header row, date format and amount columns • works out whether spending is negative or positive • tidies merchant names • skips transfers, card payments and entries already in your ledger • sorts into your categories using your own past choices first • spots subscriptions and rent that repeat.
          </div>
        </div>
      )}

      {stage === "reading" && (
        <div className="text-center py-16 text-ivory-dim text-sm">Reading your statement…</div>
      )}

      {stage === "review" && view && (
        <div>
          <div className="card-hero rounded-2xl p-4 mb-3">
            <div className="text-xs text-ivory-dim mb-1 truncate">{fileName}</div>
            <div className="flex items-end justify-between">
              <div>
                <div className="v-mono text-2xl font-medium">{view.expenses.length} {view.expenses.length === 1 ? "expense" : "expenses"}</div>
                {view.from && <div className="text-xs text-ivory-dim mt-0.5">{prettyLong(view.from)} – {prettyLong(view.to)}</div>}
              </div>
              <div className="v-mono text-lg text-brass">{fmt(view.total)}</div>
            </div>
          </div>

          <div className="text-xs text-ivory-dim mb-4 leading-relaxed">
            {[
              view.dupCount > 0 && `${view.dupCount} already in your ledger`,
              view.skipped.filter((r) => r.kind === "transfer").length > 0 && `${view.skipped.filter((r) => r.kind === "transfer").length} transfers/card payments skipped`,
              view.skipped.filter((r) => r.kind === "income").length > 0 && `${view.skipped.filter((r) => r.kind === "income").length} deposits skipped`,
            ].filter(Boolean).join(" · ") || "Nothing skipped."}
          </div>

          {detection?.source === "text" && (
            <div className="bg-card rounded-xl p-3.5 mb-4 text-xs leading-relaxed">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-ivory font-medium">Statement date</div>
                  <div className="text-ivory-dim">
                    {stmtOverride || detection.statementDate
                      ? "Dates take their year from this. Month and day come from each transaction."
                      : detection.statementYear
                        ? `No statement date found — using ${detection.statementYear}. Set one if the years look wrong.`
                        : "No statement date found. Set one if the years look wrong."}
                  </div>
                </div>
                <input
                  type="date"
                  value={stmtOverride || detection.statementDate || ""}
                  onChange={(e) => applyStatementDate(e.target.value)}
                  className="rounded-md px-2 py-1 text-xs shrink-0"
                  style={selectStyle}
                />
              </div>
            </div>
          )}

          <button onClick={() => setShowHow((v) => !v)} className="text-xs text-ivory-dim underline mb-3">{showHow ? "Hide" : "How was this read?"}</button>
          {showHow && detection && (
            <div className="bg-card rounded-xl p-3.5 mb-4 text-xs leading-relaxed">
              <div className="text-ivory-dim">Date: <span className="text-ivory">{detection.date}</span>{detection.dateOrder ? <span className="text-ivory-dim"> ({detection.dateOrder === "dmy" ? "day first" : "month first"})</span> : null}</div>
              <div className="text-ivory-dim">Amount: <span className="text-ivory">{detection.amount}</span></div>
              <div className="text-ivory-dim">Description: <span className="text-ivory">{detection.description || "—"}</span></div>
              {detection.category && <div className="text-ivory-dim">Category: <span className="text-ivory">{detection.category}</span></div>}
              {detection.sheets > 1 && <div className="text-ivory-dim">Sheets read: <span className="text-ivory">{detection.sheets}</span></div>}
              <div className="text-ivory-dim">Spending looks like: <span className="text-ivory">{detection.spendingIs}</span></div>
              <label className="flex items-center gap-2 mt-3 text-ivory-dim">
                <input type="checkbox" checked={flipped} onChange={(e) => { setFlipped(e.target.checked); setInclusion({}); }} />
                Spending looks backwards — swap what counts as money in / out
              </label>
            </div>
          )}

          {view.unsure.length > 0 && (
            <div className="mb-3">
              {renderGroup({ id: "__unsure", title: "Needs a look", dot: "var(--brass)", list: view.unsure, total: view.unsure.reduce((s, r) => s + r.amount, 0), sub: "couldn't match to a category", defaultOpen: true })}
              {isPaid ? (
                <button onClick={runAi} disabled={aiBusy} className="w-full flex items-center justify-center gap-1.5 bg-card2 rounded-xl py-2.5 text-xs font-medium disabled:opacity-50">
                  <Sparkles size={13} className="text-brass" /> {aiBusy ? "Sorting…" : `Sort these with AI (${new Set(view.unsure.map((r) => r.merchant)).size} merchants)`}
                </button>
              ) : (
                <div className="text-xs text-ivory-dim px-1">
                  Pick a category for each, or {UPGRADE_URL ? <a href={UPGRADE_URL} target="_blank" rel="noreferrer" className="text-brass font-medium">let AI sort them (paid)</a> : "use the dropdowns"}. Changing one updates every row from the same merchant, and it's remembered next import.
                </div>
              )}
              {isPaid && <div className="text-xs text-ivory-dim mt-1.5 px-1">Only merchant names are sent to the AI — never amounts or dates.</div>}
            </div>
          )}

          {view.groupList.map((g) => renderGroup({ id: g.name, title: g.name, dot: categoryColor(g.name), list: g.list, total: g.total, sub: view.newCats.includes(g.name) ? "new category" : null }))}

          {view.skipped.length > 0 && (
            <div className="mt-4">
              <button onClick={() => setShowSkipped((v) => !v)} className="flex items-center gap-1 text-xs text-ivory-dim underline">
                {showSkipped ? "Hide" : "Review"} {view.skipped.length} skipped
              </button>
              {showSkipped && (
                <div className="bg-card rounded-xl px-3.5 py-1 mt-2">
                  {view.skipped.slice(0, 200).map((r) => (
                    <div key={r.id} className="flex items-start gap-2 py-2 border-b border-hair last:border-0">
                      {(r.kind !== "income" || isBusiness) ? (
                        <input type="checkbox" checked={r.included} onChange={() => toggleRow(r)} className="shrink-0" />
                      ) : <span className="w-[13px] shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm break-words">{r.note || r.merchant}</div>
                        <div className="text-xs text-ivory-dim">{prettyLong(r.date)} · {r.duplicate ? "Already in your ledger" : r.reason}</div>
                      </div>
                      <div className="v-mono text-sm shrink-0">{fmt(r.amount)}</div>
                    </div>
                  ))}
                  {isBusiness && <div className="text-xs text-ivory-dim py-2">Ticking a deposit adds it as business income. If you also invoice that client, skip it so it isn't counted twice in Profit &amp; Loss.</div>}
                </div>
              )}
            </div>
          )}

          {view.recurring.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-ivory-dim mb-2"><Repeat size={12} /> Looks recurring</div>
              <p className="text-xs text-ivory-dim mb-3">Same amount at a steady interval. Keep these ticked to have them logged automatically from now on.</p>
              <div className="flex flex-col gap-2">
                {view.recurring.map((c) => (
                  <label key={c.key} className="flex items-center gap-3 bg-card rounded-xl px-3 py-2.5 cursor-pointer">
                    <input type="checkbox" checked={!recurringOff[c.key]} onChange={() => setRecurringOff((m) => ({ ...m, [c.key]: !m[c.key] }))} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm break-words">{c.label}</div>
                      <div className="text-xs text-ivory-dim">{c.category} · seen {c.count}×, {c.frequency}</div>
                    </div>
                    <div className="v-mono text-sm shrink-0">{fmt(c.amount)}</div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {view.newCats.length > 0 && (
            <div className="mt-5 text-xs text-ivory-dim">New categories that will be added: {view.newCats.map((c) => <span key={c} className="inline-block px-2 py-0.5 rounded-full mr-1.5 mt-1" style={{ background: "var(--brass-dim)", color: "var(--brass)" }}>{c}</span>)}</div>
          )}

          <div className="flex gap-2 mt-6">
            <button onClick={confirm} disabled={view.expenses.length === 0 && view.incomeIn.length === 0} className="flex-1 bg-gold-grad text-ink font-semibold rounded-full py-3 text-sm disabled:opacity-40">
              Import {view.expenses.length} {view.expenses.length === 1 ? "expense" : "expenses"}{view.incomeIn.length ? ` + ${view.incomeIn.length} income` : ""}
            </button>
            <button onClick={reset} className="px-4 text-xs text-ivory-dim">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
